import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'
import { isTeamPingMessage } from 'src/pages/_myAccount/Requests/Request/components/Chat/utils/teamPingMessage'

import { queryKeys } from './queryKeys'

export type ConversationRow =
  Database['public']['Tables']['conversation']['Row']
export type ConversationInsert =
  Database['public']['Tables']['conversation']['Insert']
export type ConversationUpdate =
  Database['public']['Tables']['conversation']['Update']

export type ConversationLastMessageSummary = {
  plainText: string
  attachmentCount: number
}

export type ConversationWithQuote = ConversationRow & {
  quote?: {
    id: number
    name: string | null
    public_id: string | null
    status: string | null
  } | null
  organization?: { id: string; name: string } | null
  user?: {
    id: string
    first_name: string | null
    last_name: string | null
    email: string | null
  } | null
  last_message_at?: string | null
  last_message_role?: string | null
  last_message_is_team_ping?: boolean
  message_count?: number
  last_message_summary?: ConversationLastMessageSummary | null
}

type NestedOrderMessageRow = {
  created_at: string
  content: string
  role: string
  attachment_urls: string[] | null
}

const LAST_MESSAGE_PREVIEW_MAX_CHARS = 120

const htmlToPlainText = (html: string): string => {
  if (!html.trim()) return ''
  if (typeof document === 'undefined') {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
  const el = document.createElement('div')
  el.innerHTML = html
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

const summarizeLatestOrderMessage = (
  messages: NestedOrderMessageRow[]
): ConversationLastMessageSummary | null => {
  let latest: NestedOrderMessageRow | null = null
  for (const msg of messages) {
    if (!msg?.created_at) continue
    if (!latest || msg.created_at > latest.created_at) latest = msg
  }
  if (!latest) return null

  const rawPlain = htmlToPlainText(latest.content ?? '')
  const attachmentCount =
    latest.attachment_urls?.filter((u) => typeof u === 'string' && u.trim())
      .length ?? 0

  if (!rawPlain && attachmentCount === 0) return null

  const plainText =
    rawPlain.length > LAST_MESSAGE_PREVIEW_MAX_CHARS
      ? `${rawPlain.slice(0, LAST_MESSAGE_PREVIEW_MAX_CHARS - 1)}…`
      : rawPlain

  return { plainText, attachmentCount }
}

const latestOrderMessage = (
  messages: NestedOrderMessageRow[]
): NestedOrderMessageRow | null => {
  let latest: NestedOrderMessageRow | null = null
  for (const msg of messages) {
    if (!msg?.created_at) continue
    if (!latest || msg.created_at > latest.created_at) latest = msg
  }
  return latest
}

const attachActivityFields = (
  row: ConversationWithQuote & {
    order_message?: NestedOrderMessageRow[] | null
  }
): ConversationWithQuote => {
  const messages = row.order_message ?? []
  const latest = latestOrderMessage(messages)
  const lastMessageAt = latest?.created_at ?? null
  const last_message_summary = summarizeLatestOrderMessage(messages)

  const { order_message: _omitted, ...rest } = row
  return {
    ...rest,
    last_message_at: lastMessageAt,
    last_message_role: latest?.role ?? null,
    last_message_is_team_ping: latest ? isTeamPingMessage(latest) : false,
    message_count: messages.length,
    last_message_summary,
  } as ConversationWithQuote
}

export const useConversations = ({
  org_id,
  enabled = true,
}: {
  org_id?: string | null
  enabled?: boolean
}) => {
  const supabase = useSupabaseClient()

  const { data, isPending } = useQuery({
    queryKey: [queryKeys.conversations, org_id],
    queryFn: async (): Promise<ConversationWithQuote[]> => {
      let query = supabase.from('conversation').select(
        `*,
          quote!conversation_quote_id_fkey(id, name, public_id),
          order_message(created_at, content, role, attachment_urls)`
      )

      if (org_id) query = query.eq('org_id', org_id)

      const { data, error } = await query
      if (error) throw error

      const rows = (data ?? []) as Array<
        ConversationWithQuote & {
          order_message?: NestedOrderMessageRow[] | null
        }
      >

      const withActivity = rows.map((row) => attachActivityFields(row))

      withActivity.sort((a, b) => {
        const aKey = a.last_message_at ?? a.created_at
        const bKey = b.last_message_at ?? b.created_at
        if (aKey === bKey) return 0
        return aKey < bKey ? 1 : -1
      })

      return withActivity
    },
    enabled: enabled && !!org_id,
    refetchInterval: 10_000,
  })

  return {
    conversations: data ?? [],
    isPending,
  }
}

export const useAllConversationsAdmin = ({
  enabled = true,
}: {
  enabled?: boolean
}) => {
  const supabase = useSupabaseClient()

  const { data, isPending } = useQuery({
    queryKey: [queryKeys.conversations, 'admin', 'all'],
    queryFn: async (): Promise<ConversationWithQuote[]> => {
      const { data, error } = await supabase.from('conversation').select(
        `*,
          quote!conversation_quote_id_fkey(id, name, public_id, status),
          organization!conversation_org_id_fkey(id, name),
          user!conversation_user_id_fkey(id, first_name, last_name, email),
          order_message!inner(created_at, content, role, attachment_urls)`
      )

      if (error) throw error

      const rows = (data ?? []) as Array<
        ConversationWithQuote & {
          order_message?: NestedOrderMessageRow[] | null
        }
      >

      const withActivity = rows.map((row) => attachActivityFields(row))

      withActivity.sort((a, b) => {
        const aKey = a.last_message_at ?? a.created_at
        const bKey = b.last_message_at ?? b.created_at
        if (aKey === bKey) return 0
        return aKey < bKey ? 1 : -1
      })

      return withActivity
    },
    enabled,
    refetchInterval: 10_000,
  })

  return {
    conversations: data ?? [],
    isPending,
  }
}

export const useConversationById = (id: number | undefined | null) => {
  const supabase = useSupabaseClient()

  const { data, isPending } = useQuery({
    queryKey: [queryKeys.conversations, id],
    queryFn: async (): Promise<ConversationWithQuote | null> => {
      if (!id) return null
      const { data, error } = await supabase
        .from('conversation')
        .select('*, quote!conversation_quote_id_fkey(id, name, public_id)')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as ConversationWithQuote
    },
    enabled: !!id,
  })

  return {
    conversation: data ?? null,
    isPending,
  }
}

export const useConversationByQuoteId = (
  quote_id: number | undefined | null
) => {
  const supabase = useSupabaseClient()

  const { data, isPending } = useQuery({
    queryKey: [queryKeys.conversations, 'byQuote', quote_id],
    queryFn: async (): Promise<ConversationWithQuote | null> => {
      if (!quote_id) return null

      const { data, error } = await supabase
        .from('conversation')
        .select('*, quote!conversation_quote_id_fkey(id, name, public_id)')
        .eq('quote_id', quote_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return (data as ConversationWithQuote | null) ?? null
    },
    enabled: !!quote_id,
    refetchInterval: 10_000,
  })

  return {
    conversation: data ?? null,
    isPending,
  }
}

export const useCreateConversation = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const createConversation = useMutation({
    mutationFn: async (params: ConversationInsert) => {
      const { data, error } = await supabase
        .from('conversation')
        .insert(params)
        .select('*, quote!conversation_quote_id_fkey(id, name, public_id)')
        .single()

      if (error) throw error
      return data as ConversationWithQuote
    },
    onSuccess: (created, variables) => {
      // Optimistically add the new conversation to the cached list so consumers
      // (e.g. the chat widget) can immediately navigate to it without waiting
      // for the refetch.
      queryClient.setQueryData<ConversationWithQuote[]>(
        [queryKeys.conversations, variables.org_id],
        (prev) => (prev ? [created, ...prev] : [created])
      )
      queryClient.invalidateQueries({
        queryKey: [queryKeys.conversations],
      })
    },
  })

  return { createConversation }
}

export const useUpdateConversation = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const updateConversation = useMutation({
    mutationFn: async (params: ConversationUpdate & { id: number }) => {
      const { id, ...updates } = params
      const { data, error } = await supabase
        .from('conversation')
        .update(updates)
        .eq('id', id)
        .select('*, quote!conversation_quote_id_fkey(id, name, public_id)')
        .single()

      if (error) throw error
      return data as ConversationWithQuote
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.conversations],
      })
    },
  })

  return { updateConversation }
}
